import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X } from 'lucide-react';
import { Link } from 'react-router-dom';

export function CookieBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const { i18n } = useTranslation();

  const getText = (fr, ar, en) => {
    if (i18n.language === 'fr') return fr;
    if (i18n.language === 'ar') return ar;
    return en;
  };

  useEffect(() => {
    // Check if user has already made a choice
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      // Show banner after a short delay
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptCookies = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    setIsVisible(false);
    // Enable Google Analytics
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }
  };

  const declineCookies = () => {
    localStorage.setItem('cookieConsent', 'declined');
    setIsVisible(false);
    // Disable Google Analytics
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied'
      });
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
          data-testid="cookie-banner"
        >
          <div className="max-w-4xl mx-auto bg-[#1a1a1a] rounded-2xl shadow-2xl border border-white/10 overflow-hidden">
            <div className="p-6 md:p-8">
              <div className="flex items-start gap-4">
                {/* Icon */}
                <div className="hidden sm:flex w-12 h-12 rounded-full bg-[#f4b00f]/10 items-center justify-center flex-shrink-0">
                  <Cookie className="w-6 h-6 text-[#f4b00f]" />
                </div>

                {/* Content */}
                <div className="flex-1">
                  <h3 className="text-white font-semibold text-lg mb-2 flex items-center gap-2">
                    <Cookie className="w-5 h-5 text-[#f4b00f] sm:hidden" />
                    {getText('Nous utilisons des cookies', 'نستخدم ملفات تعريف الارتباط', 'We use cookies')}
                  </h3>
                  <p className="text-white/60 text-sm leading-relaxed mb-4">
                    {getText(
                      'Ce site utilise des cookies pour améliorer votre expérience et analyser le trafic. En cliquant sur "Accepter", vous consentez à l\'utilisation de tous les cookies.',
                      'يستخدم هذا الموقع ملفات تعريف الارتباط لتحسين تجربتك وتحليل حركة المرور. بالنقر على "قبول"، فإنك توافق على استخدام جميع ملفات تعريف الارتباط.',
                      'This site uses cookies to improve your experience and analyze traffic. By clicking "Accept", you consent to the use of all cookies.'
                    )}
                    {' '}
                    <Link 
                      to="/confidentialite" 
                      className="text-[#f4b00f] hover:underline"
                    >
                      {getText('En savoir plus', 'اعرف المزيد', 'Learn more')}
                    </Link>
                  </p>

                  {/* Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={acceptCookies}
                      className="px-6 py-2.5 bg-[#f4b00f] hover:bg-[#d99a00] text-black font-medium rounded-full transition-colors text-sm"
                      data-testid="cookie-accept-btn"
                    >
                      {getText('Accepter', 'قبول', 'Accept')}
                    </button>
                    <button
                      onClick={declineCookies}
                      className="px-6 py-2.5 bg-white/10 hover:bg-white/20 text-white font-medium rounded-full transition-colors text-sm"
                      data-testid="cookie-decline-btn"
                    >
                      {getText('Refuser', 'رفض', 'Decline')}
                    </button>
                  </div>
                </div>

                {/* Close button */}
                <button
                  onClick={declineCookies}
                  className="text-white/40 hover:text-white transition-colors p-1"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default CookieBanner;
